import { useEffect, useState } from 'react';

/**
 * Tracks which section heading is currently at or above the top of the viewport.
 * Uses getBoundingClientRect for accurate position detection regardless of
 * CSS scroll-margin, nested containers, or other layout quirks.
 */
export function useActiveSection(sectionIds: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(sectionIds[0] ?? null);

  useEffect(() => {
    if (sectionIds.length === 0) return;

    function onScroll() {
      // A section becomes "active" once its top edge enters the upper third
      // of the viewport. This ensures the highlight updates as soon as the
      // heading is clearly visible, not only when it reaches the very top.
      const threshold = window.innerHeight * 0.35;
      let current: string | null = null;

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (!el) continue;

        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          current = id;
        }
      }

      setActiveId(current ?? sectionIds[0]);
    }

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sectionIds]);

  return activeId;
}
